import type { DeadlineDomainValue } from '@prospero/shared';
import { useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import {
  DeadlineService,
  type IDeadlineService,
} from '../../../services/deadline/deadline.service';
import type { DeadlineUrlFilter } from './deadline-filter-url';
import { deadlinesQueryKeys } from './deadline-query-keys';

export const EXPECTED_DEADLINES_PAGE_SIZE = 20;

/** "Documenti attesi": scadenze il cui ciclo corrente non ha ancora un file. */
export function useExpectedDeadlines(options: {
  enabled: boolean;
  page: number;
  assigneeUserId?: string;
  domain?: DeadlineDomainValue;
}) {
  const service = useInjection<IDeadlineService>(DeadlineService);
  return useQuery({
    queryKey: deadlinesQueryKeys.expected(options.page, options.assigneeUserId, options.domain),
    queryFn: () =>
      service.findAll({
        withoutDocument: true,
        assigneeUserId: options.assigneeUserId || undefined,
        domain: options.domain,
        page: options.page,
        pageSize: EXPECTED_DEADLINES_PAGE_SIZE,
      }),
    enabled: options.enabled,
  });
}

export const FILTERED_DEADLINES_PAGE_SIZE = 20;

interface FilteredDeadlinesOptions {
  filter: DeadlineUrlFilter;
  q: string;
  assigneeUserId: string;
  domain: DeadlineDomainValue | '';
  page: number;
}

/** "Scadenze filtrate": scadenze con o senza file, per stato e/o periodo (link dell'assistente). */
export function useFilteredDeadlines(options: FilteredDeadlinesOptions) {
  const service = useInjection<IDeadlineService>(DeadlineService);
  const { filter, q, assigneeUserId, domain, page } = options;
  return useQuery({
    queryKey: deadlinesQueryKeys.filtered({
      bucket: filter.status,
      dueFrom: filter.from,
      dueTo: filter.to,
      q,
      assigneeUserId,
      domain,
      page,
    }),
    queryFn: () =>
      service.findAll({
        bucket: filter.status || undefined,
        dueFrom: filter.from || undefined,
        dueTo: filter.to || undefined,
        q: q || undefined,
        assigneeUserId: assigneeUserId || undefined,
        domain: domain || undefined,
        page,
        pageSize: FILTERED_DEADLINES_PAGE_SIZE,
      }),
  });
}

export function useDeadlineAssigneeCandidates(options: { enabled: boolean }) {
  const service = useInjection<IDeadlineService>(DeadlineService);
  return useQuery({
    queryKey: deadlinesQueryKeys.assigneeCandidates(),
    queryFn: () => service.findAssigneeCandidates(),
    enabled: options.enabled,
    staleTime: 60_000,
  });
}

export function useDeadlineDetail(deadlineId: string | null) {
  const service = useInjection<IDeadlineService>(DeadlineService);
  return useQuery({
    queryKey: deadlinesQueryKeys.detail(deadlineId ?? ''),
    queryFn: () => service.findOne(deadlineId as string),
    enabled: deadlineId !== null,
  });
}
